/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import { Timeslot } from 'types/entities/Timeslot'
import { PackageCategory } from 'types/enums/PackageCategory'
import BookingAction from 'store/booking/BookingAction'
import {
    getCurrentDateIndex,
    getSelectedCreditPackagesForOffer,
    getSelectedCreditPackages,
    getSelectedUpsellPackages,
    getTimeslotsAvailable,
    usePackageItemKartingOptions,
} from 'utils/packageUtils'
import { useTypedSelector } from 'helpers/reducers'
import { useAppDispatch } from 'helpers/store'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'utils/toastUtils'
import { DEFAULT_HALL_ID } from 'helpers/config'
import { getVoucherCodes } from 'utils/voucherUtils'
import { useDebouncedDispatch } from 'utils/useDebounceQueue'
import VoucherAction from 'store/shop/VoucherAction'
// import {useReactGoogleAnalytics} from "utils/useReactGoogleAnalytics";
// import {GAEventName} from "types/entities/specific/GAEventName";
import { useHallOpeningHours } from 'utils/hooks/useHallOpeningHours'
import { PackagesCart } from 'types/entities/PackagesCart'
import { Reservation } from 'types/entities/Reservation'
import { ReservationOrigin } from 'types/enums/ReservationOrigin'
import type { Offer } from 'types/entities/Offer'
import { OffersAction } from 'store/offers/OffersAction'
import UiAlertConfirmationModal from 'components/Ui/UiAlertConfirmationModal'
import { closeSecondaryModal, openSecondaryModal } from 'helpers/actions'
import { ModalSize } from 'types/enums/ModalSize'
import { UiButtonStyle } from 'components/Ui/UiButton'
import Dates from 'utils/Dates'
import {getSelectedBookingCredits} from 'utils/booking/creditItems'
import {hasValidFamilyCreditOwner} from 'utils/booking/packageItemPayload'
import { enrichReservationsWithCompanies } from 'utils/reservations/enrichReservationsWithCompanies'

export interface UseSellerTimeslotsReturn {
    availableDates: Date[]
    bookWithAutomaticTimeslot: (
        timeslot?: Timeslot | null,
        forEvent?: boolean
    ) => void
    createOfferWithUpsells: (offer: Offer, forEvent?: boolean) => void
    prependOoohWarningToBooking: (
        timeslot?: Timeslot | null,
        forEvent?: boolean
    ) => void
    currentDateIndex: number
    customSelectedTimeslots: Timeslot[]
    firstPossibleSlotIndex: number | null
    isLoadingTimeslots: boolean
    isFetchingTimeslot: boolean
    lastPossibleSlotIndex: number | null
    resetCustomSelectedTimeslotHandler: () => void
    selectCustomSlotsEnabled: boolean
    selectedTimeslot: Timeslot | null
    setSelectCustomSlotsEnabledHandler: (enable: boolean) => void
    setSelectedTimeslotHandler: (
        timeslot: Timeslot | null,
        selectCustomSlotsEnabled: boolean
    ) => void
    setStepsEnabledHandler: (value: boolean) => void
    stepsEnabled: boolean
    switchDate: (date: string) => void
    timeslotsAvailable: (Timeslot & { isOooh: boolean })[]
    isLoading: boolean
    finishHandler: (offer?: Offer | undefined) => void
    reservationsForDate: Reservation[]
    isLoadingReservations: boolean
    possibleTimeslots: Timeslot[]
}

const useSellerTimeslots = (
    setTimeslot: (timeslot: Timeslot) => void,
    _navigate: any,
    _scrollRefs: any,
    finishHandler: (offer?: Offer | undefined) => void,
    _seller_id: number | null | undefined, // Fixed type
    isCalculationMode?: boolean,
    disableAutoFetch?: boolean
): UseSellerTimeslotsReturn => {
    // Added return type
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const [isLoading, setIsLoading] = useState(false)
    const timeslots = useTypedSelector((state) => state.bookingState.timeslots)
    const filter = useTypedSelector(
        (state) => state.bookingState.bookingProcess.filter
    )
    const packageItemsSelectedTimeslot = useTypedSelector(
        (state) => state.bookingState.bookingProcess.selectedTimeslot
    )

    const hall = useTypedSelector((state) => state.hallState.hall)
    const sessionSellerId = useTypedSelector(
        (state) => state.sessionState.session?.uid
    )
    const packagesCart = useTypedSelector(
        (state) => state.bookingState.bookingProcess.packageCart
    )
    // const selectedPackageCategory = useTypedSelector(
    //     (state) => state.bookingState.bookingProcess.filter.category
    // )
    const selectedUpsells = useTypedSelector(
        (state) => state.bookingState.bookingProcess.upsell
    )
    const creditProcess = useTypedSelector((state) => state.bookingState.bookingProcess)
    const selectedCredits = getSelectedBookingCredits(creditProcess)

    const [availableDates, _setAvailableDates] = useState<Date[]>([])
    const [customSelectedTimeslots, setCustomSelectedTimeslots] = useState<
        Timeslot[]
    >([])
    const [firstPossibleSlotIndex, setFirstPossibleSlotIndex] = useState<
        number | null
    >(null)
    const [isLoadingTimeslots, setIsLoadingTimeslots] = useState(true)
    const [isFetchingTimeslot, setIsFetchingTimeslot] = useState(false)
    const [lastPossibleSlotIndex, setLastPossibleSlotIndex] = useState<
        number | null
    >(null)
    const [selectCustomSlotsEnabled, setSelectCustomSlotsEnabled] =
        useState<boolean>(false)
    const [selectedTimeslot, setSelectedTimeslot] = useState<Timeslot | null>(
        null
    )
    const [stepsEnabled, setStepsEnabled] = useState<boolean>(false)
    const voucherItems = useTypedSelector(
        (state) => state.voucherState.voucherItems
    )
    const { debouncedDispatch } = useDebouncedDispatch()
    // const {sendGenericEvent} = useReactGoogleAnalytics();
    const currentDateIndex = getCurrentDateIndex(availableDates, filter)
    const selectedUpsellPackages = getSelectedUpsellPackages(selectedUpsells)
    const selectedCreditPackages = getSelectedCreditPackages(selectedCredits, filter.adults)
    const selectedCreditPackagesForOffer = getSelectedCreditPackagesForOffer(
        selectedCredits,
        filter.adults,
        filter.children
    )
    const [possibleTimeslots, _setPossibleTimeslots] = useState<Timeslot[]>([])

    const { timeslotOutsideOpeningHours } = useHallOpeningHours()

    const timeslotsAvailable = useMemo(() => {
        const slotsForSelectedDate = filter.date
            ? getTimeslotsAvailable(timeslots, hall).filter(
                  (timeslot) => Dates.ymd(new Date(timeslot.start_at)) === filter.date
              )
            : []

        return slotsForSelectedDate.map(
            (timeslot: Timeslot) => {
                const timeslotDate: Date = new Date(timeslot.start_at)
                const tooh = timeslotOutsideOpeningHours(timeslotDate)
                return {
                    ...timeslot,
                    isOooh: tooh,
                }
            }
        )
    }, [timeslots, hall, filter.date, timeslotOutsideOpeningHours])
    const [reservationsResult, setReservationsResult] = useState<{
        date: string | null
        reservations: Reservation[]
        isLoading: boolean
    }>({ date: null, reservations: [], isLoading: false })
    const reservationsForDate = useMemo(
        () =>
            reservationsResult.date === filter.date
                ? reservationsResult.reservations
                : [],
        [filter.date, reservationsResult]
    )
    const isLoadingReservations = Boolean(
        !disableAutoFetch &&
            filter.date &&
            sessionSellerId &&
            (reservationsResult.date !== filter.date || reservationsResult.isLoading)
    )
    const { getBookingPackageItems, getBookingPackageItemsForEvent } =
        usePackageItemKartingOptions()
    // const { getCategoryBySlug } = usePackageCategory()

    const resetCustomSelectedTimeslotHandler = () => {
        setFirstPossibleSlotIndex(null)
        setLastPossibleSlotIndex(null)
        return dispatch(BookingAction.resetCustomTimeslot())
    }

    const setStepsEnabledHandler = (value: boolean) => {
        if (value === true) {
            resetCustomSelectedTimeslotHandler()
            setSelectedTimeslot(null)
            setPossibleTimeslotsHandler([])
            setFirstPossibleSlotIndex(null)
            setLastPossibleSlotIndex(null)
            setCustomSelectedTimeslots([])
        }
        setStepsEnabled(value)
    }
    const setPossibleTimeslotsHandler = (timeslots: Timeslot[]) => {
        return dispatch(BookingAction.setPossibleTimeslots(timeslots))
    }
    const setSelectCustomSlotsEnabledHandler = (enable: boolean) => {
        //provide general reset functionality if this is ever switched back
        resetCustomSelectedTimeslotHandler()
        setSelectCustomSlotsEnabled(enable)

        setSelectedTimeslot(null)
        setPossibleTimeslotsHandler([])
        setFirstPossibleSlotIndex(null)
        setLastPossibleSlotIndex(null)
        setSelectedTimeslotHandler(selectedTimeslot, enable)
    }

    const setSelectedTimeslotHandler = (
        timeslot: Timeslot | null,
        _selectCustomSlotsEnabled: boolean
    ) => {
        toast.dismiss()
        setSelectedTimeslot(timeslot)
    }

    const fetchPackagesOnDateChange = () => {
        const voucherCodes = getVoucherCodes(voucherItems)
        dispatch(VoucherAction.resetAppliedVouchers())
        debouncedDispatch(
            BookingAction.fetchPackagesByLocationParams(
                window.location,
                filter,
                true,
                voucherCodes
            )
        )
    }
    const switchDate = (date: string) => {
        setSelectCustomSlotsEnabledHandler(false)
        dispatch(
            BookingAction.setFilter({
                ...filter,
                date: date,
            })
        )
        fetchPackagesOnDateChange()
    }

    const sendGAData = (packagesCartRes: PackagesCart) => {
        if (!packagesCartRes?.packagesCartItems) {
            return
        }

        // const items: DataLayerItem[] = packagesCartRes.packagesCartItems.map(
        //     (packageItem) => {
        //         const category = filter.category
        //         const item: DataLayerItem = {
        //             item_id: packageItem.package_id,
        //             item_name: packageItem?.packageItem
        //                 ? translate(packageItem.packageItem.name)
        //                 : getPackageCategoryName(category ? category : ''),
        //             quantity:
        //                 packageItem.drivers_count_adult +
        //                 packageItem.drivers_count_child,
        //             item_variant: '',
        //             price:
        //                 packageItem.price /
        //                 (packageItem.drivers_count_adult +
        //                     packageItem.drivers_count_child),
        //             currency: CURRENCY,
        //         }
        //         return item
        //     }
        // )

        // sendGenericEvent(GAEventName.AddToCart, packagesCartRes.price, items);
    }

    const prependOoohWarningToBooking = (
        timeslot?: Timeslot | null,
        forEvent: boolean = false
    ) => {
        if (timeslot?.isOooh) {
            return dispatch(
                openSecondaryModal(
                    <UiAlertConfirmationModal
                        title={t('Timeslot is outside of opening hours')}
                        message={t('Are you sure you want to book this time slot?')}
                        onConfirm={() => {
                            dispatch(closeSecondaryModal())
                            bookWithAutomaticTimeslot(timeslot, forEvent)
                        }}
                        onCancel={() => dispatch(closeSecondaryModal())}
                        confirmStyle={UiButtonStyle.Primary}
                    />,
                    { modalSize: ModalSize.Small },
                    t('Confirm')
                )
            )
        }
        return bookWithAutomaticTimeslot(timeslot, forEvent)
    }
    const bookWithAutomaticTimeslot = (
        timeslot?: Timeslot | null,
        forEvent: boolean = false
    ) => {
        // if (!packages_cart) {
        //     return;
        // }
        const package_item_ids = forEvent
            ? getBookingPackageItemsForEvent()
            : getBookingPackageItems()
        if (timeslot) {
            dispatch(BookingAction.setPackageItemsSelectedTimeslot(timeslot))
            //@todo replace with proper function if needed
            // history.push(
            //     '/booking/seller/new-reservation' +
            //         '?' +
            //         appendQueryParams(filter)
            // )
            selectTimeslotHandler(timeslot)
            setIsFetchingTimeslot(false)
            if (!packagesCart) {
                dispatch(BookingAction.initCart(true)).finally(() => {
                    setIsLoading(false)
                })
            }
            return
        }

        if (
            isFetchingTimeslot ||
            !filter.category ||
            !packagesCart ||
            !packageItemsSelectedTimeslot ||
            !sessionSellerId
        ) {
            console.log(
                'something isnt',
                isFetchingTimeslot,
                filter.category,
                packagesCart,
                packageItemsSelectedTimeslot,
                sessionSellerId
            )
            return
        }

        if (!hasValidFamilyCreditOwner(selectedCredits, creditProcess.selectedPackageItems)) {
            toast.error(t('Family credits require an adult ticket as the first participant.'))
            return
        }
        setIsLoading(true)
        dispatch(
            BookingAction.fetchBookPackageNew(
                {
                    package_category_name: filter.category,
                    package_item_ids: package_item_ids,
                    activity_ids:
                        filter.activity_ids.length === 0
                            ? [1]
                            : filter.activity_ids,
                    timeslot_id: packageItemsSelectedTimeslot.id,
                    adults: filter.adults,
                    children: filter.children,
                    upsell: selectedUpsellPackages,
                    creditItems: selectedCreditPackages,
                },
                packagesCart,
                'useCustomTimeslotsNew - bookWithAutomaticTimeslot'
            )
        )
            .then((r) => {
                //we are here

                setIsFetchingTimeslot(false)
                // sendGAData(r.packageCart);
                sendGAData(r.packageCart)
            })
            .finally(() => {
                setIsLoading(false)
                setIsFetchingTimeslot(false)
                finishHandler()
            })
        setIsFetchingTimeslot(true)
    }

    const createOfferWithUpsells = (
        offer: Offer,
        forEvent: boolean = false
    ) => {
        const package_item_ids = forEvent
            ? getBookingPackageItemsForEvent()
            : getBookingPackageItems()

        if (
            isFetchingTimeslot ||
            !filter.category ||
            !packagesCart ||
            !packageItemsSelectedTimeslot ||
            !sessionSellerId
        ) {
            console.log(
                'something isnt',
                isFetchingTimeslot,
                filter.category,
                packagesCart,
                packageItemsSelectedTimeslot,
                sessionSellerId
            )
            return
        }

        if (!hasValidFamilyCreditOwner(selectedCredits, creditProcess.selectedPackageItems)) {
            toast.error(t('Family credits require an adult ticket as the first participant.'))
            return
        }
        setIsLoading(true)
        if (isCalculationMode) {
            dispatch(
                BookingAction.fetchOfferPackageNew(
                    {
                        package_category_name: filter.category,
                        package_item_ids: package_item_ids,
                        activity_ids:
                            filter.activity_ids.length === 0
                                ? [1]
                                : filter.activity_ids,
                        timeslot_id: packageItemsSelectedTimeslot.id,
                        adults: filter.adults,
                        children: filter.children,
                        upsell: selectedUpsellPackages,
                        creditItems: selectedCreditPackagesForOffer,
                        seller_id: sessionSellerId,
                    },
                    packagesCart,
                    offer,
                    'useSellerTimeslots - createOfferWithUpsells'
                )
            ).then(() => {
                dispatch(OffersAction.fetchOfferById(offer.id)).then(
                    (updatedOffer: Offer) => {
                        setIsFetchingTimeslot(false)
                        setIsLoading(false)
                        finishHandler(updatedOffer)
                    }
                ).catch(() => {
                    setIsFetchingTimeslot(false)
                    setIsLoading(false)
                    finishHandler(offer)
                })
            }).catch(() => {
                setIsFetchingTimeslot(false)
                setIsLoading(false)
                finishHandler(offer)
            })
            setIsFetchingTimeslot(true)
        } else {
            dispatch(
                BookingAction.fetchOfferPackageNew(
                    {
                        package_category_name: filter.category,
                        package_item_ids: package_item_ids,
                        activity_ids:
                            filter.activity_ids.length === 0
                                ? [1]
                                : filter.activity_ids,
                        timeslot_id: packageItemsSelectedTimeslot.id,
                        adults: filter.adults,
                        children: filter.children,
                        upsell: selectedUpsellPackages,
                        creditItems: selectedCreditPackagesForOffer,
                        seller_id: sessionSellerId,
                    },
                    packagesCart,
                    offer,
                    'useCustomTimeslotsNew - bookWithAutomaticTimeslot'
                )
            )
                .then((r) => {
                    finishHandler(r.offer)
                })
                .finally(() => {
                    setIsLoading(false)
                    setIsFetchingTimeslot(false)
                })
            setIsFetchingTimeslot(true)
        }
    }

    const selectTimeslotHandler = (timeslot: Timeslot) => {
        setTimeslot(timeslot)
    }
    const timeslotsFetchIdRef = useRef(0)
    const reservationsFetchIdRef = useRef(0)
    const activityIdsRequestKey = filter.activity_ids.join(',')

    useEffect(() => {
        const fetchId = ++timeslotsFetchIdRef.current

        if (disableAutoFetch || !filter.date) {
            setIsLoadingTimeslots(false)
            if (!filter.date) {
                dispatch(BookingAction.setTimeslots([]))
            }
            return
        }

        const requestedDate = filter.date
        setIsLoadingTimeslots(true)

        const timedFetch = setTimeout(() => {
            dispatch(
                BookingAction.fetchCategoryTimeSlots(
                    {
                        adults: filter.adults,
                        children: filter.children,
                        date: requestedDate,
                        package_category_name: filter.category || PackageCategory.General,
                        hall_id: DEFAULT_HALL_ID,
                        activity_ids: filter.activity_ids.length === 0 ? [1] : filter.activity_ids,
                        timeslot_id: selectedTimeslot?.id ?? null,
                    },
                    sessionSellerId,
                    { storeResult: false }
                )
            )
                .then((response) => {
                    if (fetchId !== timeslotsFetchIdRef.current) {
                        return
                    }

                    dispatch(BookingAction.setTimeslots(response.timeslots))
                    setIsLoadingTimeslots(false)
                })
                .catch(() => {
                    if (fetchId !== timeslotsFetchIdRef.current) {
                        return
                    }

                    dispatch(BookingAction.setTimeslots([]))
                    setIsLoadingTimeslots(false)
                })
        }, 250)

        return () => {
            clearTimeout(timedFetch)
            if (fetchId === timeslotsFetchIdRef.current) {
                timeslotsFetchIdRef.current += 1
            }
        }
    }, [
        activityIdsRequestKey,
        disableAutoFetch,
        dispatch,
        filter.adults,
        filter.category,
        filter.children,
        filter.date,
        sessionSellerId,
    ])

    useEffect(() => {
        const fetchId = ++reservationsFetchIdRef.current
        const requestedDate = filter.date || null

        if (disableAutoFetch || !requestedDate || !sessionSellerId) {
            setReservationsResult({ date: requestedDate, reservations: [], isLoading: false })
            return
        }

        setReservationsResult({ date: requestedDate, reservations: [], isLoading: true })

        dispatch(
            BookingAction.fetchReservationsForDate(requestedDate, sessionSellerId)
        )
            .then(async (response) => {
                if (fetchId !== reservationsFetchIdRef.current || !Array.isArray(response.reservations)) {
                    return
                }

                const filteredReservations = response.reservations.filter((reservation) => {
                    if (reservation.origin === ReservationOrigin.Online) {
                        return true
                    }
                    return reservation.seller_id !== null && reservation.seller_id !== undefined
                })
                const enrichedReservations = await enrichReservationsWithCompanies(filteredReservations, dispatch)

                if (fetchId !== reservationsFetchIdRef.current) {
                    return
                }

                setReservationsResult({
                    date: requestedDate,
                    reservations: enrichedReservations,
                    isLoading: false,
                })
            })
            .catch(() => {
                if (fetchId === reservationsFetchIdRef.current) {
                    setReservationsResult({ date: requestedDate, reservations: [], isLoading: false })
                }
            })

        return () => {
            if (fetchId === reservationsFetchIdRef.current) {
                reservationsFetchIdRef.current += 1
            }
        }
    }, [disableAutoFetch, dispatch, filter.date, sessionSellerId])

    return {
        availableDates,
        bookWithAutomaticTimeslot,
        createOfferWithUpsells,
        prependOoohWarningToBooking,
        currentDateIndex,
        customSelectedTimeslots,
        firstPossibleSlotIndex,
        isLoadingTimeslots,
        isFetchingTimeslot,
        lastPossibleSlotIndex,
        resetCustomSelectedTimeslotHandler,
        selectCustomSlotsEnabled,
        selectedTimeslot,
        setSelectCustomSlotsEnabledHandler,
        setSelectedTimeslotHandler,
        setStepsEnabledHandler,
        stepsEnabled,
        switchDate,
        timeslotsAvailable,
        isLoading,
        finishHandler,
        reservationsForDate,
        isLoadingReservations,
        possibleTimeslots,
    }
}

export default useSellerTimeslots
